"use client";

import {
  useLayoutEffect,
  useRef,
  type ComponentProps,
  type ReactNode,
  type RefObject,
} from "react";
import { Loader2, Send } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/** 입력칸 최대 높이 — 줄 높이 20px 기준 약 4.6줄. 넘으면 칸 안에서 스크롤. */
export const COMPOSER_MAX_HEIGHT = 108;

/** 알약 안 원형 버튼(32px, 누르는 영역 40px). 색은 쓰는 쪽에서 덧붙인다. */
export const GLASS_ICON_BUTTON =
  "relative flex size-8 shrink-0 items-center justify-center rounded-full transition-colors after:absolute after:-inset-1 after:content-['']";

/** 흰 원 버튼(+ 등). */
export const GLASS_WHITE_BUTTON = cn(
  GLASS_ICON_BUTTON,
  "bg-white text-gray-900 shadow-[0_1px_2px_rgba(17,24,39,0.1)] active:bg-gray-100 disabled:text-gray-300",
);

type TextareaExtraProps = Omit<
  ComponentProps<"textarea">,
  "value" | "onChange" | "ref" | "placeholder" | "disabled" | "className" | "style"
>;

interface GlassComposerProps {
  value: string;
  onChange: (value: string) => void;
  /** 전송 버튼 · Cmd/Ctrl+Enter. Enter 는 줄바꿈. */
  onSend: () => void;
  canSend: boolean;
  sending?: boolean;
  placeholder: string;
  sendLabel: string;
  disabled?: boolean;
  /** 글자 수 초과 등 — 테두리를 빨갛게. */
  invalid?: boolean;
  /** 입력칸 왼쪽 버튼(+). */
  leading?: ReactNode;
  textareaRef?: RefObject<HTMLTextAreaElement | null>;
  textareaProps?: TextareaExtraProps;
  /**
   * floating = 대화 위에 떠 있는 모바일 입력창(그림자).
   * docked = 대화 아래에 붙은 입력창(그림자 없이 위쪽 빛 반사선만).
   */
  variant?: "floating" | "docked";
  className?: string;
}

/**
 * 채팅 공용 글래스 입력창 — 고객 채팅 · 운영팀 문의가 함께 쓴다(admin 과 같은 모양).
 * 흰 대화 위에서 보이도록 연회색 유리. backdrop-filter 가 없으면 불투명 연회색.
 */
export function GlassComposer({
  value,
  onChange,
  onSend,
  canSend,
  sending = false,
  placeholder,
  sendLabel,
  disabled = false,
  invalid = false,
  leading,
  textareaRef,
  textareaProps,
  variant = "floating",
  className,
}: GlassComposerProps) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const ref = textareaRef ?? innerRef;

  // 입력칸 높이를 글에 맞춘다(최대 COMPOSER_MAX_HEIGHT, 넘으면 칸 안에서 스크롤).
  // CSS field-sizing 은 iOS 웹뷰 지원이 불확실해서 직접 잰다.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
  }, [value, ref]);

  return (
    <div
      className={cn(
        "flex items-end gap-1.5 rounded-3xl border bg-[#E9EBEE] p-[7px]",
        "supports-[backdrop-filter]:bg-[#E2E5E9]/65 supports-[backdrop-filter]:backdrop-blur-[20px] supports-[backdrop-filter]:backdrop-saturate-[1.8]",
        invalid ? "border-red-400" : "border-gray-900/6",
        variant === "floating"
          ? "shadow-[0_6px_20px_-8px_rgba(17,24,39,0.28),inset_0_1px_0_rgba(255,255,255,0.7)]"
          : "shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]",
        disabled && "opacity-60",
        className,
      )}
    >
      {leading}

      <Textarea
        {...textareaProps}
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          textareaProps?.onKeyDown?.(e);
          if (e.defaultPrevented) return;
          if (
            e.key === "Enter" &&
            (e.metaKey || e.ctrlKey) &&
            !e.nativeEvent.isComposing
          ) {
            e.preventDefault();
            if (canSend) onSend();
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        // Enter = 줄바꿈. 발송은 전송 버튼으로만.
        enterKeyHint="enter"
        rows={1}
        className="min-h-8 min-w-0 flex-1 resize-none overflow-y-auto overscroll-contain rounded-none border-0 bg-transparent px-1 py-1.5 text-sm leading-5 shadow-none focus-visible:ring-0 dark:bg-transparent"
        style={{ maxHeight: COMPOSER_MAX_HEIGHT }}
      />

      <button
        type="button"
        onClick={onSend}
        disabled={!canSend}
        className={cn(
          GLASS_ICON_BUTTON,
          canSend
            ? "bg-orange-600 text-white active:bg-orange-700"
            : "bg-gray-900/8 text-gray-400",
        )}
        title={sendLabel}
        aria-label={sendLabel}
      >
        {sending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Send className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  );
}
